import { FC } from 'react';

import Link from 'next/link';
import router from 'next/router';

import { Collapse, useMediaQuery } from '@mui/material';

import { IoMenuSharp } from 'react-icons/io5';
import { MdClose } from 'react-icons/md';
import { useQuery } from 'react-query';
import { useRecoilValue } from 'recoil';

import { useDashboardData } from '@hooks_iqfu/dashboard/useDashboardData';

import { SideMenuArrow } from '@components_iqfu//icons/SideMenuArrow';

import { companyLoginUserDataAtom, companyLoginUserDataSelector } from '@recoil_iqfu/atoms/auth/companyLoginUserData';

import { MediaQuery } from '@constants_common';
import { TargetMenu, useMenuToggle } from '@hooks_common/useMenuToggle';

import styles from './SideMenu.module.scss';

type Props = {
  toggleSpMenu: () => void;
  spMenuOpen: boolean;
};

const CompanySideMenu: FC<Props> = ({ toggleSpMenu, spMenuOpen }) => {
  const { getCompanyDashboardData } = useDashboardData();
  const { data: dashboardData } = useQuery(['getCompanyDashboardData'], () => getCompanyDashboardData());

  const companyLoginUserData = useRecoilValue(companyLoginUserDataAtom);
  const { companyId, isMultipleCompanyUser } = useRecoilValue(companyLoginUserDataSelector);

  const {
    iqfuMenuToggleState: { openPro, openIqfu, openChat, openJusetsu, openOthers },
    iqfuMenuToggleHandler,
  } = useMenuToggle();

  const serviceList = companyLoginUserData?.services;
  const companyType = companyLoginUserData?.company_type;

  const hasIqfuService = serviceList?.includes('iqfu');
  const hasProService = serviceList?.includes('pro');
  const hasIqloudService = serviceList?.includes('cloud');
  const hasAiAppraisalService = serviceList?.includes('ai_appraisal');
  const hasSellerChat = serviceList?.includes('seller_chat');
  const hasGeneralCompanyPurchaseCompanyChat = serviceList?.includes('general_company_purchase_company_chat');
  const hasFinancialAgencyConsulting = serviceList?.includes('financial_consulting');
  const hasVisitReservationService = serviceList?.includes('visit_reservation');
  const hasUserVoiceService = serviceList?.includes('user_voice');

  const hasUnreadChat =
    dashboardData &&
    (dashboardData.unread_selling_room_count > 0 ||
      dashboardData.unread_seller_room_count > 0 ||
      dashboardData.unread_inquiry_count > 0 ||
      dashboardData.unread_consulting_room_count > 0);
  const isMobile = useMediaQuery(MediaQuery.sm);
  const isVisible = isMultipleCompanyUser || (!isMultipleCompanyUser && !isMobile);

  return (
    <div className={`${styles.container} ${spMenuOpen && styles.sp_open}`}>
      <div className={`${styles.container_inner}`}>
        <div className={styles.sp_header}>
          <div className={styles.logo_wrap}>
            {!spMenuOpen ? (
              <Link href={`/console/company/dashboard`}>
                <a>
                  <img src="/images/iqfu/dashboard/logo.svg" alt="イクラ不動産" />
                </a>
              </Link>
            ) : (
              <div />
            )}
            {spMenuOpen && <MdClose onClick={toggleSpMenu} />}
            {!spMenuOpen && <IoMenuSharp onClick={toggleSpMenu} />}
          </div>
        </div>
        <div className={styles.menu_container}>
          {(hasProService || hasAiAppraisalService) && isVisible && (
            <>
              <div
                className={`${styles.sideMenuTitle} ${styles.sideMenuTitle_pro}`}
                onClick={() => iqfuMenuToggleHandler(TargetMenu.PRO)}
              >
                イクラ不動産Pro
                <SideMenuArrow
                  circleFill="#eb5a2a"
                  className={`${styles.menuToggleIcon} ${openPro && styles.menuToggleIcon_isOpen}`}
                />
              </div>
              <Collapse in={openPro}>
                <div className={`${styles.sideMenuItemsWrapper} ${styles.sideMenuItemsWrapper_iqfu}`}>
                  {hasProService && (
                    <>
                      <div className={styles.sideMenuItem}>
                        <Link href={`/console/company/iqfu_pro`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${
                                router.pathname === `/console/company/iqfu_pro` ? styles.current : ''
                              }`}
                            >
                              成約事例検索
                            </div>
                          </a>
                        </Link>
                      </div>
                      <div className={styles.sideMenuItem}>
                        <Link href={`/console/company/iqfu_pro/map`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${
                                router.pathname === `/console/company/iqfu_pro/map` ? styles.current : ''
                              }`}
                            >
                              成約事例Map検索
                            </div>
                          </a>
                        </Link>
                      </div>
                    </>
                  )}
                  {hasAiAppraisalService && (
                    <>
                      <div className={styles.sideMenuItem}>
                        <Link href={`/console/company/ai_appraisal_reports/list`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${
                                router.pathname === `/console/company/ai_appraisal_reports/list` ? styles.current : ''
                              }`}
                            >
                              AI査定書一覧
                            </div>
                          </a>
                        </Link>
                      </div>
                      <div className={styles.sideMenuItem}>
                        <Link href={`/console/company/ai_appraisal_reports/create`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${
                                router.pathname === `/console/company/ai_appraisal_reports/create` ? styles.current : ''
                              }`}
                            >
                              AI査定書新規作成
                            </div>
                          </a>
                        </Link>
                      </div>
                    </>
                  )}
                </div>
              </Collapse>
            </>
          )}

          {hasIqfuService && isVisible && (
            <>
              <div
                className={`${styles.sideMenuTitle} ${styles.sideMenuTitle_iqfu}`}
                onClick={() => iqfuMenuToggleHandler(TargetMenu.IQFU)}
              >
                イクラ不動産
                <SideMenuArrow
                  circleFill="#fe9210"
                  className={`${styles.menuToggleIcon} ${openIqfu && styles.menuToggleIcon_isOpen}`}
                />
              </div>
              <Collapse in={openIqfu}>
                <div className={`${styles.sideMenuItemsWrapper} ${styles.sideMenuItemsWrapper_iqfu}`}>
                  <div className={`${styles.sideMenuItem}`}>
                    <Link href={`/console/company/dashboard`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/company/dashboard` ? styles.current : ''
                          }`}
                        >
                          管理画面トップ
                        </div>
                      </a>
                    </Link>
                  </div>
                  <div className={`${styles.sideMenuItem}`}>
                    <Link href={`/console/company/simulator`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/company/simulator` ? styles.current : ''
                          }`}
                        >
                          相場シミュレーター
                        </div>
                      </a>
                    </Link>
                  </div>
                  <div className={styles.sideMenuItem}>
                    <Link href={`/console/company/sale_cases/create`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/company/sale_cases/create` ? styles.current : ''
                          }`}
                        >
                          売却実績登録
                        </div>
                      </a>
                    </Link>
                  </div>
                  <div className={`${styles.sideMenuItem}`}>
                    <Link href={`/console/company/sale_cases`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/company/sale_cases` ? styles.current : ''
                          }`}
                        >
                          売却実績一覧
                        </div>
                      </a>
                    </Link>
                  </div>
                  {isMultipleCompanyUser ? (
                    <div className={`${styles.sideMenuItem}`}>
                      <Link href={`/console/company/companies_list`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/company/companies_list` ? styles.current : ''
                            }`}
                          >
                            会社一覧
                          </div>
                        </a>
                      </Link>
                    </div>
                  ) : (
                    <div className={`${styles.sideMenuItem}`}>
                      <Link href={`/console/company/company_detail/${companyId}`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === '/console/company/company_detail/[company_id]' ? styles.current : ''
                            }`}
                          >
                            会社情報
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  {isMultipleCompanyUser ? (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/company/staffs_list`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === '/console/company/staffs_list' ? styles.current : ''
                            }`}
                          >
                            スタッフ紹介
                          </div>
                        </a>
                      </Link>
                    </div>
                  ) : (
                    <div className={`${styles.sideMenuItem}`}>
                      <Link href={`/console/company/company_detail/${companyId}/staffs`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === '/console/company/company_detail/[company_id]/staffs'
                                ? styles.current
                                : ''
                            }`}
                          >
                            スタッフ紹介
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  {isMultipleCompanyUser ? (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/company/notification_emails_list`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/company/notification_emails_list` ? styles.current : ''
                            }`}
                          >
                            通知先一覧
                          </div>
                        </a>
                      </Link>
                    </div>
                  ) : (
                    <div className={`${styles.sideMenuItem}`}>
                      <Link href={`/console/company/notification_emails/${companyId}`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/company/notification_emails/[company_id]`
                                ? styles.current
                                : ''
                            }`}
                          >
                            通知先一覧
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  <div className={`${styles.sideMenuItem}`}>
                    <Link href={`/console/company/announcements`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/company/announcements` ? styles.current : ''
                          }`}
                        >
                          お知らせ
                        </div>
                      </a>
                    </Link>
                  </div>
                  {hasVisitReservationService && (
                    <div className={`${styles.sideMenuItem}`}>
                      <Link href={`/console/company/visit_reservation_bookings`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/company/visit_reservation_bookings` ? styles.current : ''
                            }`}
                          >
                            来店予約一覧
                            <span className={styles.newIcon}>NEW</span>
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                </div>
              </Collapse>
            </>
          )}

          {hasIqfuService && (
            <>
              <div
                className={`${styles.sideMenuTitle} ${styles.sideMenuTitle_chat}`}
                onClick={() => iqfuMenuToggleHandler(TargetMenu.CHAT)}
              >
                <div>
                  売却相談チャット
                  {hasUnreadChat && (
                    <img src="/images/iqfu/dashboard/unread_icon.svg" alt="未読" className={styles.menu_unread_icon} />
                  )}
                </div>
                <SideMenuArrow
                  circleFill="#17c69c"
                  className={`${styles.menuToggleIcon} ${openChat && styles.menuToggleIcon_isOpen}`}
                />
              </div>
              <Collapse in={openChat}>
                <div className={`${styles.sideMenuItemsWrapper} ${styles.sideMenuItemsWrapper_chat}`}>
                  <div className={styles.sideMenuItem}>
                    <Link href={`/console/company/chat/talk`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/company/chat/talk` ? styles.current : ''
                          }`}
                        >
                          売却相談
                          {dashboardData && dashboardData?.unread_selling_room_count > 0 && (
                            <img
                              src="/images/iqfu/dashboard/unread_icon.svg"
                              alt="未読"
                              className={styles.menu_unread_icon}
                            />
                          )}
                        </div>
                      </a>
                    </Link>
                  </div>
                  <div className={styles.sideMenuItem}>
                    <Link href={`/console/company/customers`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname.startsWith('/console/company/customers') &&
                            !router.pathname.includes('property_detail')
                              ? styles.current
                              : ''
                          }`}
                        >
                          顧客一覧<span className={styles.newIcon}>NEW</span>
                        </div>
                      </a>
                    </Link>
                  </div>
                  {hasSellerChat && isVisible && (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/company/chat/seller_contact`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/company/chat/seller_contact` ? styles.current : ''
                            }`}
                          >
                            リースバック紹介売主チャット
                            {dashboardData && dashboardData?.unread_seller_room_count > 0 && (
                              <img
                                src="/images/iqfu/dashboard/unread_icon.svg"
                                alt="未読"
                                className={styles.menu_unread_icon}
                              />
                            )}
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  <div className={styles.sideMenuItem}>
                    <Link href={`/console/company/chat/contact`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/company/chat/contact` ? styles.current : ''
                          }`}
                        >
                          運営問合せ
                          {dashboardData && dashboardData?.unread_inquiry_count > 0 && (
                            <img
                              src="/images/iqfu/dashboard/unread_icon.svg"
                              alt="未読"
                              className={styles.menu_unread_icon}
                            />
                          )}
                        </div>
                      </a>
                    </Link>
                  </div>
                  {hasFinancialAgencyConsulting && isVisible && (
                    <div className={`${styles.sideMenuItem}`}>
                      <Link href={`/console/company/chat/consulting`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/company/chat/consulting` ? styles.current : ''
                            }`}
                          >
                            金融機関チャット
                            {dashboardData && dashboardData?.unread_consulting_room_count > 0 && (
                              <img
                                src="/images/iqfu/dashboard/unread_icon.svg"
                                alt="未読"
                                className={styles.menu_unread_icon}
                              />
                            )}
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  {hasGeneralCompanyPurchaseCompanyChat && isVisible && (
                    <div className={`${styles.sideMenuItem}`}>
                      <Link href={`/console/company/chat/general_company_purchase_company_chat`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/company/chat/general_company_purchase_company_chat`
                                ? styles.current
                                : ''
                            }`}
                          >
                            {companyType === 'general_agency' ? '買取専門会社チャット' : '仲介会社チャット'}
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  {isVisible && (
                    <>
                      <div className={`${styles.sideMenuItem}`}>
                        <Link href={`/console/company/agreement_fee_simulator`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${
                                router.pathname === `/console/company/agreement_fee_simulator` ? styles.current : ''
                              }`}
                            >
                              成約手数料シミュレーター
                              <span className={styles.newIcon}>NEW</span>
                            </div>
                          </a>
                        </Link>
                      </div>
                      <div className={`${styles.sideMenuItem}`}>
                        <Link href={`/console/company/chat/mediation_agreement_reports/create`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${
                                router.pathname === `/console/company/chat/mediation_agreement_reports/create`
                                  ? styles.current
                                  : ''
                              }`}
                            >
                              媒介契約報告
                            </div>
                          </a>
                        </Link>
                      </div>
                      <div className={`${styles.sideMenuItem}`}>
                        <Link href={`/console/company/chat/mediation_agreement_reports`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${
                                router.pathname === `/console/company/chat/mediation_agreement_reports`
                                  ? styles.current
                                  : ''
                              }`}
                            >
                              媒介契約報告一覧
                            </div>
                          </a>
                        </Link>
                      </div>
                      <div className={`${styles.sideMenuItem}`}>
                        <Link href={`/console/company/chat/agreement_reports/create`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${
                                router.pathname === `/console/company/chat/agreement_reports/create`
                                  ? styles.current
                                  : ''
                              }`}
                            >
                              売買契約報告
                            </div>
                          </a>
                        </Link>
                      </div>
                      <div className={`${styles.sideMenuItem}`}>
                        <Link href={`/console/company/chat/agreement_reports`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${
                                router.pathname === `/console/company/chat/agreement_reports` ? styles.current : ''
                              }`}
                            >
                              売買契約報告一覧
                            </div>
                          </a>
                        </Link>
                      </div>
                    </>
                  )}
                </div>
              </Collapse>
            </>
          )}

          {hasIqloudService && isVisible && (
            <>
              <div
                className={`${styles.sideMenuTitle} ${styles.sideMenuTitle_iqloud}`}
                onClick={() => iqfuMenuToggleHandler(TargetMenu.JUSETSU)}
              >
                重説イクラウド
                <SideMenuArrow
                  circleFill="#0985c8"
                  className={`${styles.menuToggleIcon} ${openJusetsu && styles.menuToggleIcon_isOpen}`}
                />
              </div>

              <Collapse in={openJusetsu}>
                <div className={`${styles.sideMenuItemsWrapper} ${styles.sideMenuItemsWrapper_iqloud}`}>
                  <div className={`${styles.sideMenuItem}`}>
                    <Link href={`/console/company/iqra_cloud`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/company/iqra_cloud` ? styles.current : ''
                          }`}
                        >
                          トップ
                        </div>
                      </a>
                    </Link>
                  </div>
                  <div className={`${styles.sideMenuItem}`}>
                    <Link href={`/console/company/iqra_cloud/jusetsu/list`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/company/iqra_cloud/jusetsu/list` ? styles.current : ''
                          }`}
                          data-test="companySideMenuLink-list"
                        >
                          重説一覧
                        </div>
                      </a>
                    </Link>
                  </div>
                  {!isMultipleCompanyUser && (
                    <div className={`${styles.sideMenuItem}`}>
                      <Link href={`/console/company/iqra_cloud/jusetsu/upload`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/company/iqra_cloud/jusetsu/upload` ? styles.current : ''
                            }`}
                            data-test="companySideMenuLink-upload"
                          >
                            重説アップロード
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                </div>
              </Collapse>
            </>
          )}

          {hasUserVoiceService && (
            <>
              <div
                className={`${styles.sideMenuTitle} ${styles.sideMenuTitle_others}`}
                onClick={() => iqfuMenuToggleHandler(TargetMenu.OTHERS)}
              >
                その他
                <SideMenuArrow
                  circleFill="#99471f"
                  className={`${styles.menuToggleIcon} ${openOthers && styles.menuToggleIcon_isOpen}`}
                />
              </div>
              <Collapse in={openOthers}>
                <div className={`${styles.sideMenuItemsWrapper} ${styles.sideMenuItemsWrapper_others}`}>
                  <div className={`${styles.sideMenuItem}`}>
                    <Link href={`/console/company/user_voice_setting`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/company/user_voice_setting` ? styles.current : ''
                          }`}
                        >
                          口コミ各種設定
                          <span className={styles.newIcon}>NEW</span>
                        </div>
                      </a>
                    </Link>
                  </div>
                  <div className={`${styles.sideMenuItem}`}>
                    <Link href={`/console/company/user_voice_reviews`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/company/user_voice_reviews` ? styles.current : ''
                          }`}
                        >
                          口コミイクラ反映一覧
                          <span className={styles.newIcon}>NEW</span>
                        </div>
                      </a>
                    </Link>
                  </div>
                  <div className={`${styles.sideMenuItem}`}>
                    <Link href={`/console/company/user_voice_google_reviews`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/company/user_voice_google_reviews` ? styles.current : ''
                          }`}
                        >
                          口コミHP反映一覧
                          <span className={styles.newIcon}>NEW</span>
                        </div>
                      </a>
                    </Link>
                  </div>
                </div>
              </Collapse>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
export default CompanySideMenu;
